// @ts-nocheck react-table is not very typescript friendly
/* eslint-disable react/jsx-key */

import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { makeStyles } from '@mui/styles'
import createStyles from '@mui/styles/createStyles'
import usePrevious from 'hooks/usePrevious'
import { isEqual } from 'lodash'
// import useHideColumns from 'hooks/useHideColumns'
import { isFunction } from 'lodash-es'
import { forwardRef, Fragment, useEffect, useRef, useState } from 'react'
import {
  useAsyncDebounce,
  useExpanded,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import 'regenerator-runtime/runtime'
import { generateRandomKey } from 'utils/random'
import SkeletonRow from './SkeletonRow'
import { TableProps } from './types'

const styles = createStyles<any, TableProps>({
  paginationRowsSelect: {
    display: 'flex',
    alignItems: 'center',
  },
  noRecords: { textAlign: 'center' },
})

const useStyles = makeStyles(styles)
const defaultPropGetter = () => ({})

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef()
  const resolvedRef = ref || defaultRef

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate
  }, [resolvedRef, indeterminate])

  return <Checkbox size="small" ref={resolvedRef} {...rest} />
})

const MainTable = (props: TableProps) => {
  const classes = useStyles(props)

  const {
    id = 'table',
    loading,
    hooksAvailable,
    columns,
    data: initialData,
    onChange,
    stickyHeader = true,
    horizontalScroll = false,
    getCellProps = defaultPropGetter,
    getRowProps = defaultPropGetter,
    getHeaderProps = defaultPropGetter,
    getTableProps: getUserTableProps = defaultPropGetter,
    renderToolbar,
    getExpandableComponent,
    paginationProps = {
      rowsPerPage: 10,
      rowsPerPageOptions: [10, 25, 50, 100],
    },
    onFuzzySearch,
  } = props

  const [data, setData] = useState([])

  const prevData = usePrevious(data)

  useEffect(() => {
    if (!initialData) return
    if (isEqual(data, initialData)) return
    setData(initialData)
  }, [initialData])

  useEffect(() => {
    if (isEqual(data, prevData)) return
    onChange?.(data)
  }, [data])

  const updateData = (rowIndex, columnId, value) => {
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          }
        }
        return row
      })
    )
  }

  const removeRow = (rowIndex) => {
    setData((old) => old.filter((_, index) => index !== rowIndex))
  }

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    visibleColumns,
    rows,
    setGlobalFilter,
    toggleAllRowsExpanded,
  } = useTable(
    {
      id,
      columns,
      data,
      autoResetGlobalFilter: false,
      autoResetSortBy: false,
      autoResetExpanded: false,
      paginateExpandedRows: false,
      initialState: {
        pageSize: paginationProps?.rowsPerPage ?? 10,
      },
      updateData,
      removeRow,
    },
    // useHideColumns,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (hooksAvailable) {
        {
          hooks.visibleColumns.push((columns) => [
            // Let's make a column for selection
            {
              id: 'selection',
              style: {
                width: 0,
              },
              // The header can use the table's getToggleAllRowsSelectedProps method
              // to render a checkbox
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              ),
              // The cell can use the individual row's getToggleRowSelectedProps method
              // to the render a checkbox
              Cell: ({ row }) => (
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              ),
            },
            ...columns,
          ])
        }
      }
    }
  )

  // Magically, using react state speeds up the search and typing
  // does not block the main thread anymore, therefore better UX
  const [search] = useState(globalFilter)
  const handleSearchChange = useAsyncDebounce(setGlobalFilter, 200)

  useEffect(() => {
    if (isFunction(onFuzzySearch)) {
      onFuzzySearch(page)
    }
  }, [search, page])

  return (
    <Box id={id}>
      {isFunction(renderToolbar) &&
        renderToolbar({
          globalSearch: { value: search, onChange: handleSearchChange },
        })}
      <div style={{ overflow: horizontalScroll ? 'scroll' : null }}>
        <Table
          stickyHeader={stickyHeader}
          {...getTableProps([getUserTableProps()])}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  {...column.getHeaderProps([
                    column.getSortByToggleProps(),
                    { style: column.style },
                    getHeaderProps(column),
                  ])}
                >
                  {column.canSort ? (
                    <TableSortLabel
                      style={{ width: 'max-content' }}
                      active={column.isSorted}
                      direction={column.isSortedDesc ? 'desc' : 'asc'}
                    >
                      {column.render('Header')}
                    </TableSortLabel>
                  ) : (
                    column.render('Header')
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            {...getTableBodyProps()}
            data-testid={loading ? null : `${id}-data-rows`}
          >
            {loading &&
              !data.length &&
              new Array(3)
                .fill(0)
                .map(() => (
                  <SkeletonRow
                    key={`skeleton-row-${generateRandomKey(10)}`}
                    cells={visibleColumns.length}
                  />
                ))}

            {page.map((row: ReactTableRow) => {
              prepareRow(row)

              const expandProps = isFunction(getExpandableComponent)
                ? {
                    onClick: () => {
                      if (!row.isExpanded) toggleAllRowsExpanded(false)
                      row.toggleRowExpanded()
                    },
                    expanded: row.isExpanded ? 'true' : null,
                  }
                : {}

              return (
                <Fragment key={`fragment_${row.id}`}>
                  {row.isExpanded
                    ? getDummyRow(visibleColumns.length, true)
                    : null}

                  <TableRow
                    {...row.getRowProps([getRowProps(row), expandProps])}
                  >
                    {row.cells.map((cell) => (
                      <TableCell
                        {...cell.getCellProps([
                          { style: cell.column.style },
                          getCellProps(cell.column.original),
                        ])}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                  </TableRow>

                  {row.isExpanded && isFunction(getExpandableComponent)
                    ? [
                        <TableRow key={`row_${row.id}_sub`}>
                          <TableCell
                            colSpan={visibleColumns.length}
                            padding="none"
                          >
                            {getExpandableComponent(row)}
                          </TableCell>
                        </TableRow>,
                        getDummyRow(visibleColumns.length),
                      ]
                    : null}
                </Fragment>
              )
            })}
            {rows.length === 0 && !loading ? (
              <TableRow nohover="true">
                <TableCell
                  colSpan={visibleColumns.length}
                  className={classes.noRecords}
                >
                  No records to display
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
      {paginationProps &&
        (paginationProps.isServerPagination ? (
          <TablePagination
            component="div"
            count={paginationProps.totalPages}
            slotProps={{
              actions: {
                nextButton: {
                  disabled:
                    paginationProps.totalPages ===
                    paginationProps.currentPage + 1,
                },
              },
            }}
            onPageChange={(_, page) => {
              paginationProps.onPageChange(page)
            }}
            onRowsPerPageChange={(e) => {
              setPageSize(e.target.value || 10)
              paginationProps.onRowsPerPageChange(Number(e.target.value))
            }}
            page={paginationProps.currentPage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={paginationProps.rowsPerPageOptions}
            classes={{ select: classes.paginationRowsSelect }}
            labelDisplayedRows={({ count }) => {
              return `${paginationProps.currentPage + 1} of ${count}`
            }}
          />
        ) : (
          <TablePagination
            component="div"
            count={rows.length}
            onPageChange={(_, page) => gotoPage(page)}
            onRowsPerPageChange={(e) => setPageSize(e.target.value || 10)}
            page={pageIndex}
            rowsPerPage={pageSize}
            rowsPerPageOptions={paginationProps.rowsPerPageOptions}
            classes={{ select: classes.paginationRowsSelect }}
          />
        ))}
    </Box>
  )
}

export default MainTable

const getDummyRow = (colSpan: number, top?: boolean) => {
  return (
    <TableRow
      key={`row_dummy_${top ? 'top' : 'bottom'}`}
      style={{ borderTop: top ? null : '0.5px solid #bbb' }}
      nohover="true"
    >
      <td
        colSpan={colSpan}
        style={{
          backgroundColor: '#f0f3f6',
          height: 15,
        }}
      ></td>
    </TableRow>
  )
}
